import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { apiClient } from '@/api/client';
import { NOTIFICATIONS_QUERY_KEYS } from '@/api/queries/notifications';
import { FRIENDS_QUERY_KEYS } from '@/api/queries/friendship';
import { notificationWs } from '@/services/notification-websocket';
import type { Notification } from '@/types/notification';
import type { NotificationsData } from '@/types/display';
import type { RealtimeNotification } from '@/services/notification-websocket';

/**
 * Hook that manages the real-time WebSocket connection for notifications.
 * - Connects when the user is authenticated and the app is in the foreground.
 * - Disconnects when the user logs out or the app goes to the background.
 * - Invalidates React Query notification caches on incoming messages.
 *
 * Should be called once in the root layout.
 */
export function useNotificationWebSocket(): void {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();
  const appState = useRef<AppStateStatus>(AppState.currentState);

  // Register the optimistic-update + background-refetch callback once
  useEffect(() => {
    notificationWs.onNotification((realtimeNotification) => {
      // 1. Optimistically increment the unread count
      queryClient.setQueryData<number>(
        NOTIFICATIONS_QUERY_KEYS.unreadCount(),
        (old) => (old ?? 0) + 1,
      );

      // 2. Optimistically prepend to the notifications list (if cached)
      queryClient.setQueryData<NotificationsData>(
        NOTIFICATIONS_QUERY_KEYS.list(),
        (old) => {
          if (!old) return old;
          const newNotification = mapRealtimeToNotification(realtimeNotification);
          return {
            notifications: [newNotification, ...old.notifications],
          };
        },
      );

      // 3. Background refetch for canonical data
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.all });

      // 4. Type-specific cache invalidations
      if (realtimeNotification.type === 'friend_request_accepted' ||
          realtimeNotification.type === 'friend_request') {
        queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEYS.all });
      }
    });
  }, [queryClient]);

  // Connect / disconnect based on auth + app state
  useEffect(() => {
    let startupDelayId: ReturnType<typeof setTimeout> | null = null;
    let isInitialConnect = true;

    const connectIfNeeded = async () => {
      if (!isLoggedIn || appState.current !== 'active') {
        notificationWs.disconnect();
        return;
      }

      const doConnect = async () => {
        try {
          const token = await apiClient.getToken();
          if (token) {
            notificationWs.connect(token);
          }
        } catch {
          // Token retrieval failed; polling fallback will cover us
        }
      };

      // Defer initial connection to let critical renders complete
      if (isInitialConnect) {
        isInitialConnect = false;
        startupDelayId = setTimeout(doConnect, 2000);
      } else {
        await doConnect();
      }
    };

    connectIfNeeded();

    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        const wasBackground = appState.current.match(/inactive|background/);
        appState.current = nextState;

        if (nextState === 'active' && wasBackground) {
          connectIfNeeded();
        } else if (nextState.match(/inactive|background/)) {
          notificationWs.disconnect();
        }
      }
    );

    return () => {
      if (startupDelayId) clearTimeout(startupDelayId);
      subscription.remove();
      notificationWs.disconnect();
    };
  }, [isLoggedIn]);
}

function mapRealtimeToNotification(rt: RealtimeNotification): Notification {
  const data = rt.data;

  const actorImage =
    typeof data.actor_image === 'string' ? data.actor_image : '';

  return {
    id: rt.id,
    type: rt.type as Notification['type'],
    content: (data.content as string) ?? '',
    time: 'just now',
    createdAt: new Date().toISOString(),
    read: false,
    user: data.actor_id
      ? {
          id: data.actor_id as string,
          name: (data.actor_name as string) ?? 'Someone',
          avatar: actorImage,
          ...(data.actor_username
            ? { username: data.actor_username as string }
            : {}),
        }
      : undefined,
    referenceType: data.reference_type as string | undefined,
    referenceId: data.reference_id as string | undefined,
    menuItemId: data.menu_item_id as string | undefined,
    menuItemName: data.menu_item_name as string | undefined,
    businessId: data.business_id as string | undefined,
    businessName: data.business_name as string | undefined,
  };
}
